import "./UserGuide.scss"

import {
  ActionList,
  Button,
  Icon,
  LegacyCard,
  Link,
  Popover,
  Text,
} from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  MenuHorizontalIcon,
  XSmallIcon,
} from "polaris-enhance"
import { ReactNode, useEffect, useRef, useState } from "react"
import { Trans } from "react-i18next"

import APIAPP from "@/api/app"
import { IUserGuide } from "@/api/app/types/user"
import APIDashboard from "@/api/dashboard"
import stores from "@/stores"

import CustomProgressBar from "../CustomProgressBar"
import {
  AddReturnPortalBody,
  CompletedDuringOnboardingList,
  EnableNotificationsBody,
  ReviewDefaultSettingsBody,
  TestReturnBody,
} from "./bodies"
import QuickSetupGroup from "./QuickSetupGroup"
import QuickSetupItem from "./QuickSetupItem"
import {
  getGuideCompletedCount,
  reportGuideStepComplete,
  reportQuickGuideComplete,
} from "./tracking"

const PORTAL_HELP_URL =
  "https://parcelwill-returns.gitbook.io/help-center/return-page/add-return-page-to-store-navigation"
const HELP_CENTER_URL = "https://parcelwill-returns.gitbook.io/help-center"

interface ItemDef {
  key: keyof IUserGuide
  title: string
  image: string
  titleSuffix?: ReactNode
  renderBody: (completed: boolean, onComplete: () => void) => ReactNode
}

function UserGuide() {
  const { UserInfoStore } = stores
  const [dismissPopoverShow, setDismissPopoverShow] = useState(false)
  const [loadingKey, setLoadingKey] = useState<string | null>(null)
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const [completedGroupExpanded, setCompletedGroupExpanded] = useState(false)
  const [getReadyGroupExpanded, setGetReadyGroupExpanded] = useState(true)

  const { guideData, guideExpand, data } = UserInfoStore
  const isSkip = !!data.skip_onboarding
  const isCompletedOnboarding = !data.show_onboarding

  const portalInfo = (
    <span
      className="flex cursor-pointer"
      onClick={(e) => {
        e.stopPropagation()
        window.open(PORTAL_HELP_URL, "_blank")
      }}
    >
      <Icon source={InfoIcon} tone="subdued" />
    </span>
  )

  const items: ItemDef[] = [
    isSkip && {
      key: "return_setting_accessed",
      title: t("ReviewDefaultReturnSettings"),
      renderBody: (completed, onComplete) => (
        <ReviewDefaultSettingsBody
          completed={completed}
          onComplete={onComplete}
        />
      ),
    },
    {
      key: "return_page_accessed",
      title: t("AddReturnPortal"),
      image: "/images/Dashboard/GuideTask-2.svg",
      titleSuffix: portalInfo,
      renderBody: (completed, onComplete) => (
        <AddReturnPortalBody completed={completed} onComplete={onComplete} />
      ),
    },
    {
      key: "notifications_accessed",
      title: t("EnableNotifications"),
      image: "/images/Dashboard/GuideTask-3.svg",
      renderBody: (completed, onComplete) => (
        <EnableNotificationsBody
          completed={completed}
          onComplete={onComplete}
        />
      ),
    },
    {
      key: "return_request_processed",
      title: t("TryProcessingATestReturn"),
      image: "/images/Dashboard/GuideTask-4.svg",
      renderBody: (completed, onComplete) => (
        <TestReturnBody completed={completed} onComplete={onComplete} />
      ),
    },
  ].filter(Boolean) as ItemDef[]

  const total = items.length
  const completedCount = items.filter((item) => guideData[item.key]).length
  const firstIncompleteKey = items.find((item) => !guideData[item.key])?.key
  const stepKeys = items.map((item) => item.key)
  const guideStepStatusRef = useRef<Partial<Record<keyof IUserGuide, boolean>>>(
    {},
  )

  useEffect(() => {
    if (touched) return
    setExpandedItemKey(firstIncompleteKey ?? null)
  }, [firstIncompleteKey, touched])

  useEffect(() => {
    items.forEach((item) => {
      if (guideStepStatusRef.current[item.key] === undefined) {
        guideStepStatusRef.current[item.key] = !!guideData[item.key]
      }
    })
  }, [guideData, items])

  async function dismissBanner() {
    UserInfoStore.setShowBanner("show_guide", 2)
    const { code } = await APIAPP.closeBanner(12)
    if (code !== 1000) {
      UserInfoStore.setShowBanner("show_guide", 1)
    }
  }

  async function toggleComplete(key: keyof IUserGuide, val: boolean) {
    setTouched(true)
    setLoadingKey(key)
    const prev = guideStepStatusRef.current[key] ?? !!guideData[key]
    UserInfoStore.setGuideField(key, val)

    if (val) {
      const idx = items.findIndex((item) => item.key === key)
      const next =
        items
          .slice(idx + 1)
          .find((item) => !UserInfoStore.guideData[item.key]) ||
        items.find((item) => !UserInfoStore.guideData[item.key])
      setExpandedItemKey(next ? next.key : null)
    }

    try {
      const res = await APIDashboard.saveGuide(key, val)
      const code = res?.data?.code
      if (code !== 1000) {
        UserInfoStore.setGuideField(key, prev)
        return
      }

      if (val && !prev) {
        const nextCompletedCount = getGuideCompletedCount(
          guideData,
          stepKeys,
          key,
          val,
        )
        reportGuideStepComplete(guideData, stepKeys, key, nextCompletedCount)
        reportQuickGuideComplete(guideData, stepKeys, nextCompletedCount)
      }
      guideStepStatusRef.current[key] = val
    } catch (e) {
      console.error("saveGuide error:", e)
      UserInfoStore.setGuideField(key, prev)
    } finally {
      setLoadingKey(null)
    }
  }

  return (
    <LegacyCard>
      <div
        className={classNames("guide-container", {
          "guide-container-expand": !guideExpand,
        })}
      >
        <div className="flex justify-between items-center mb-2">
          <Text id="dashboard-title" variant="headingMd" as="h2">
            {t("QuickSetupGuide")}
          </Text>
          <div className="flex items-center gap-4">
            <div className="cursor-pointer">
              <Popover
                active={dismissPopoverShow}
                preferredAlignment="center"
                activator={
                  <div className="flex">
                    <Button
                      variant="tertiary"
                      icon={MenuHorizontalIcon}
                      onClick={() => setDismissPopoverShow(!dismissPopoverShow)}
                    />
                  </div>
                }
                onClose={() => setDismissPopoverShow(false)}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    {
                      content: t("Dismiss"),
                      icon: XSmallIcon,
                      onAction: dismissBanner,
                    },
                  ]}
                />
              </Popover>
            </div>
            <Button
              variant="tertiary"
              icon={guideExpand ? ChevronUpIcon : ChevronDownIcon}
              onClick={() => UserInfoStore.setExpand()}
            />
          </div>
        </div>
        <CustomProgressBar step={completedCount} stepCount={total} />
      </div>

      {guideExpand && (
        <div className="p-4 px-2">
          {isCompletedOnboarding && !isSkip && (
            <QuickSetupGroup
              title={t("CompletedDuringOnboarding")}
              expanded={completedGroupExpanded}
              onToggle={() => setCompletedGroupExpanded((v) => !v)}
            >
              <CompletedDuringOnboardingList />
            </QuickSetupGroup>
          )}

          <QuickSetupGroup
            title={t("GetReadyForYourFirstReturn")}
            expanded={getReadyGroupExpanded}
            onToggle={() => setGetReadyGroupExpanded((v) => !v)}
          >
            <div className="flex flex-col gap-1">
              {items.map((item) => {
                const completed = !!guideData[item.key]
                const expanded = expandedItemKey === item.key
                return (
                  <QuickSetupItem
                    key={item.key}
                    title={item.title}
                    completed={completed}
                    expanded={expanded}
                    loading={loadingKey === item.key}
                    titleSuffix={item.titleSuffix}
                    image={
                      expanded && item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          loading="eager"
                        />
                      ) : undefined
                    }
                    onExpand={() => {
                      setTouched(true)
                      setExpandedItemKey(item.key)
                    }}
                    onToggleComplete={(next) => {
                      toggleComplete(item.key, next)
                    }}
                  >
                    {item.renderBody(completed, () => {
                      toggleComplete(item.key, true)
                    })}
                  </QuickSetupItem>
                )
              })}
            </div>
          </QuickSetupGroup>

          <div className="pt-3 px-2">
            <Text as="p" variant="bodyMd" tone="subdued">
              <Trans
                i18nKey="NeedHelpVisitOurHelpCenterOrContactUsAnytime"
                components={[
                  <Link
                    removeUnderline
                    key="help-center"
                    url={HELP_CENTER_URL}
                    external
                  />,
                  <Link
                    removeUnderline
                    key="contact-us"
                    onClick={() => window.Intercom?.("showNewMessage", "")}
                  />,
                ]}
              />
            </Text>
          </div>
        </div>
      )}
    </LegacyCard>
  )
}

export default observer(UserGuide)
